import {openDb} from "../../../Services/BrowserDatabaseIDB";
import {ensureStoresExist} from "../../../Services/BrowserDatabaseFunctions";

export function addNewSupplyItem(_event_id, _supply_item) {
    return openDb("pa", 1, ensureStoresExist)
        .then(function (db) {
            const tx = db.transaction("Events", "readwrite");
            const dataStore = tx.objectStore("Events");

            // Get the specific event by its id
            return dataStore.get(_event_id).then(event => {
                if (!event) {
                    console.error(`Event with ID ${_event_id} not found.`);
                    return false;
                }

                // Initialize supplies array if it doesn't exist
                if (!event.supplies) {
                    event.supplies = [];
                }

                // ✅ 1. Check if the item already exists by ID
                const existingIndex = event.supplies.findIndex(item =>
                    item.id === _supply_item.id ||
                    (item.temp_id && item.temp_id === _supply_item.temp_id)
                );

                if (existingIndex !== -1) {
                    event.supplies[existingIndex] = _supply_item; // Update (promotes temp to permanent)
                } else {
                    event.supplies.push(_supply_item); // New
                }

                // ✅ 3. Sanitize and Write Back
                // (JSON.parse/stringify ensures we don't try to store proxy objects)
                const sanitizedEvent = JSON.parse(JSON.stringify(event));

                return dataStore.put(sanitizedEvent).then(() => {
                    console.log(
                        `Supply item ${_supply_item.id} sync'd to IndexedDB for Event ${_event_id}.`
                    );
                    return true;
                });
            });
        })
        .catch(function (error) {
            console.error("An error occurred in addEventSupplyItem:", error);
            throw error;
        });
}

export function addEssentialSupplyItems(_event_id, _added_items) {
    if (!_added_items || _added_items.length === 0) return Promise.resolve(true);

    return openDb("pa", 1, ensureStoresExist)
        .then(function (db) {
            const tx = db.transaction("Events", "readwrite");
            const dataStore = tx.objectStore("Events");

            return dataStore.get(_event_id).then(event => {
                if (!event) {
                    console.error(`Event with ID ${_event_id} not found.`);
                    return false;
                }

                if (!event.supplies) event.supplies = [];

                // ✅ Loop through the bulk items
                _added_items.forEach(newItem => {
                    const existingIndex = event.supplies.findIndex(item =>
                        item.id === _added_items.id ||
                        (item.temp_id && item.temp_id === _added_items.temp_id)
                    );

                    if (existingIndex === -1) {
                        // Not found locally, add it
                        event.supplies.push(newItem);
                    } else {
                        // Already exists, update it with the latest server data
                        event.supplies[existingIndex] = newItem;
                    }
                });

                // ✅ Sanitize and Write Back
                const sanitizedEvent = JSON.parse(JSON.stringify(event));

                return dataStore.put(sanitizedEvent).then(() => {
                    console.log(`Successfully sync'd ${_added_items.length} supply items to IndexedDB.`);
                    return true;
                });
            });
        })
        .catch(function (error) {
            console.error("An error occurred in addEssentialSupplyItems:", error);
            throw error;
        });
}

export function updateSupplyItem(_event_id, _supply_item) {
    return openDb("pa", 1, ensureStoresExist)
        .then(function (db) {
            const tx = db.transaction("Events", "readwrite");
            const dataStore = tx.objectStore("Events");

            return dataStore.get(_event_id).then(event => {
                if (!event) {
                    console.error(`Local update failed: Event ${_event_id} not found.`);
                    return false;
                }

                if (!event.supplies) event.supplies = [];

                // Find the item by ID (since it's an update, it must have a permanent ID)
                const existingIndex = event.supplies.findIndex(item => item.id === _supply_item.id);

                if (existingIndex !== -1) {
                    // Update the item at the specific index
                    event.supplies[existingIndex] = _supply_item;
                } else {
                    // Fallback: If for some reason it's not there, add it
                    event.supplies.push(_supply_item);
                }

                // Sanitize and save
                const sanitizedEvent = JSON.parse(JSON.stringify(event));
                return dataStore.put(sanitizedEvent).then(() => {
                    console.log(`Supply item ${_supply_item.id} updated in IndexedDB.`);
                    return true;
                });
            });
        })
        .catch(function (error) {
            console.error("Error in updateSupplyItem:", error);
            throw error;
        });
}

export function updateSupplyItemPrivacy(_event_id, _item_id, _private) {
    return openDb("pa", 1, ensureStoresExist)
        .then(function (db) {
            const tx = db.transaction("Events", "readwrite");
            const dataStore = tx.objectStore("Events");

            // Get the specific event by its id
            return dataStore.get(_event_id).then(event => {
                if (!event) {
                    console.error(`Event with ID ${_event_id} not found.`);
                    return false;
                }

                // Initialize supplies array if it doesn't exist
                if (!event.supplies) {
                    event.supplies = [];
                }

                // ✅ 1. Check if the item already exists by ID
                const existingIndex = event.supplies.findIndex(item => item.id === _item_id.id);

                if (existingIndex !== -1) {
                    // Update the privacy field directly
                    event.supplies[existingIndex].private = _private;

                    // Write back to IndexedDB
                    const sanitizedEvent = JSON.parse(JSON.stringify(event));
                    return dataStore.put(sanitizedEvent).then(() => {
                        console.log(`Privacy for item ${_item_id} updated in IndexedDB.`);
                        return true;
                    });
                } else {
                    console.warn(`Item ${_item_id} not found in local IndexedDB.`);
                    return false;
                }
            });
        })
        .catch(function (error) {
            console.error("An error occurred in addEventSupplyItem:", error);
            throw error;
        });
}

export function updateSupplyItemStatus(_event_id, _item_id, _status) {
    return openDb("pa", 1, ensureStoresExist)
        .then(function (db) {
            const tx = db.transaction("Events", "readwrite");
            const dataStore = tx.objectStore("Events");

            // Get the specific event by its id
            return dataStore.get(_event_id).then(event => {
                if (!event) {
                    console.error(`Event with ID ${_event_id} not found.`);
                    return false;
                }

                // Initialize supplies array if it doesn't exist
                if (!event.supplies) {
                    event.supplies = [];
                }

                // ✅ 1. Check if the item already exists by ID
                const existingIndex = event.supplies.findIndex(item => item.id === _item_id.id);

                if (existingIndex !== -1) {
                    // Update the status field directly
                    event.supplies[existingIndex].status = _status;

                    // Write back to IndexedDB
                    const sanitizedEvent = JSON.parse(JSON.stringify(event));
                    return dataStore.put(sanitizedEvent).then(() => {
                        console.log(`Status for item ${_item_id} updated in IndexedDB.`);
                        return true;
                    });
                } else {
                    console.warn(`Item ${_item_id} not found in local IndexedDB.`);
                    return false;
                }
            });
        })
        .catch(function (error) {
            console.error("An error occurred in addEventSupplyItem:", error);
            throw error;
        });
}

export function deleteSupplyItem(_event_id, _item_id) {
    return openDb("pa", 1, ensureStoresExist)
        .then(function (db) {
            const tx = db.transaction("Events", "readwrite");
            const dataStore = tx.objectStore("Events");

            // 1. Get the current event document
            return dataStore.get(_event_id).then(event => {
                if (!event || !event.supplies) {
                    console.warn(`Event ${_event_id} not found or has no supplies.`);
                    return false;
                }

                // 2. Filter out the item with the matching ID
                const initialLength = event.supplies.length;
                event.supplies = event.supplies.filter(item => item.id !== _item_id);

                // Check if anything actually changed before writing back
                if (event.supplies.length === initialLength) {
                    console.log(`Item ${_item_id} not found in IndexedDB. No deletion needed.`);
                    return true;
                }

                // 3. Sanitize and Save
                const sanitizedEvent = JSON.parse(JSON.stringify(event));
                return dataStore.put(sanitizedEvent).then(() => {
                    console.log(`Supply item ${_item_id} deleted from IndexedDB.`);
                    return true;
                });
            });
        })
        .catch(function (error) {
            console.error("An error occurred in deleteSupplyItem:", error);
            throw error;
        });
}